import React, { useState } from 'react'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { useSourceMetricsWithTrends, useSchemaDrift, useTimeSeriesTrend } from '../../../../hooks/useDataQuality'
import { MetricType } from '../../../../types/dataQuality'
import LoadingSkeleton from '../../../common/LoadingSkeleton'
import ErrorDisplay from '../../../common/ErrorDisplay'
import FileDetailsHeader from './file-details/FileDetailsHeader'
import SchemaStabilitySection from './file-details/SchemaStabilitySection'
import TrendAnalysisSection from './file-details/TrendAnalysisSection'
import FileMetricsRow from './file-details/FileMetricsRow'
import MetricsGrid from './file-details/MetricsGrid'
import ErrorsSection from './file-details/ErrorsSection'
import UsedBySection from './file-details/UsedBySection'
import SchemaSection from './file-details/SchemaSection'
import './SourceDetailView.css'

interface FileDetailsViewProps {
  source: any
  onBack: () => void
}

const FileDetailsView: React.FC<FileDetailsViewProps> = ({ source, onBack }) => {
  const [schemaTimeRange, setSchemaTimeRange] = useState<'week' | 'month' | 'quarter' | 'year'>('week')
  const [trendDaysBack, setTrendDaysBack] = useState(7)
  const [trendTimeRange, setTrendTimeRange] = useState<'24h' | '7d' | '30d'>('7d')

  // Fetch source metrics with trends (Endpoint #17) - Using profile-based approach
  const { data: metricsData, isLoading: isMetricsLoading, error: metricsError } = useSourceMetricsWithTrends(
    source?.source_id || null,
    {
      comparison_days: 7,
      include_detailed_trends: false,
      profile: 'complete'  // Includes all fields: error_breakdown, consumers, schema_fields, latest_file, file_health, file_sla_status, source_impact, error_rate_time_series
    }
  )

  // Fetch schema drift analysis (Endpoint #14)
  // Use schemaTimeRange directly as it matches the API's time_window values
  const { data: schemaDriftData, isLoading: isSchemaLoading } = useSchemaDrift(
    source?.source_id || null,
    { time_window: schemaTimeRange }
  )

  // Fetch time series data for Completeness chart (Endpoint #18)
  const { data: completenessData, isLoading: isCompletenessLoading } = useTimeSeriesTrend(
    {
      metric_type: MetricType.COMPLETENESS_SCORE,
      data_source_id: source?.source_id,
      days_back: trendDaysBack,
      granularity: 'daily',
      include_trend_line: true
    },
    !!source?.source_id
  )

  // Fetch time series data for Timeliness chart (Endpoint #18)
  const { data: timelinessData } = useTimeSeriesTrend(
    {
      metric_type: MetricType.FRESHNESS_SCORE,
      data_source_id: source?.source_id,
      days_back: trendDaysBack,
      granularity: 'daily',
      include_trend_line: true
    },
    !!source?.source_id
  )

  // Fetch time series data for Pipeline Success chart (Endpoint #18)
  const { data: pipelineSuccessData } = useTimeSeriesTrend(
    {
      metric_type: MetricType.PIPELINE_SUCCESS_SCORE,
      data_source_id: source?.source_id,
      days_back: trendDaysBack,
      granularity: 'daily',
      include_trend_line: true
    },
    !!source?.source_id
  )

  // Fetch time series data for Trust Score chart (Endpoint #18)
  const { data: trustScoreData } = useTimeSeriesTrend(
    {
      metric_type: MetricType.TRUST_SCORE,
      data_source_id: source?.source_id,
      days_back: trendDaysBack,
      granularity: 'daily',
      include_trend_line: true
    },
    !!source?.source_id
  )

  // Helper functions
  const getMetricValue = (value?: number, defaultValue: number = 0): number => {
    return value ?? defaultValue
  }

  const formatTimestamp = (timestamp?: string): string => {
    if (!timestamp) return 'N/A'
    
    const date = new Date(timestamp)
    
    // Format: YYYY-MM-DD HH:MM UTC
    const year = date.getUTCFullYear()
    const month = String(date.getUTCMonth() + 1).padStart(2, '0')
    const day = String(date.getUTCDate()).padStart(2, '0')
    const hours = String(date.getUTCHours()).padStart(2, '0')
    const minutes = String(date.getUTCMinutes()).padStart(2, '0')
    
    return `${year}-${month}-${day} ${hours}:${minutes} UTC`
  }

  const formatChartData = (data: any) => {
    if (!data?.chart_data) return []
    return data.chart_data.map((point: any) => ({
      time: new Date(point.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      value: point.value
    }))
  }

  const getTrendIndicator = (trend: any) => {
    if (!trend) return { percentage: 0, direction: 'stable', isPositive: false }
    const percentage = trend.percentage_change || 0
    const direction = trend.trend_direction || 'stable'
    // For error rate, negative is good; for others, positive is good
    return {
      percentage: Math.abs(percentage),
      direction,
      isPositive: percentage >= 0
    }
  }

  // Loading state
  if (isMetricsLoading) {
    return (
      <div className="file-details-view">
        <div className="file-details-header">
          <button className="back-btn" onClick={onBack}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 5L7 12L14 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="8" y1="12" x2="20" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <span className="breadcrumb-text">{source?.source_name}</span>
          </button>
        </div>
        <LoadingSkeleton variant="card" count={3} />
      </div>
    )
  }

  // Error state
  if (metricsError) {
    return (
      <div className="file-details-view">
        <div className="file-details-header">
          <button className="back-btn" onClick={onBack}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 5L7 12L14 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="8" y1="12" x2="20" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <span className="breadcrumb-text">{source?.source_name}</span>
          </button>
        </div>
        <ErrorDisplay
          error={metricsError.message}
          onRetry={() => globalThis.location.reload()}
          title="Failed to Load File Details"
        />
      </div>
    )
  }

  // Extract values from API
  const trustScore = getMetricValue(metricsData?.metrics?.trust_score?.current_value, 0)
  const recordCount = metricsData?.summary?.total_records_processed ?? 0  // Show 0 if backend returns 0
  const lastLoaded = formatTimestamp(metricsData?.calculation_timestamp)
  const completenessScore = getMetricValue(metricsData?.metrics?.completeness_score?.current_value, 0)
  const timelinessScore = getMetricValue(metricsData?.summary?.freshness_percentage, 0)
  const qualityScore = getMetricValue(metricsData?.metrics?.quality_rules_pass_rate?.current_value, 0)
  const schemaScore = getMetricValue(metricsData?.schema_drift?.stability_score, 0)
  const duplicatesScore = getMetricValue(metricsData?.summary?.duplicate_percentage, 0)
  const schemaDriftChanges = metricsData?.schema_drift?.total_changes ?? 0  // Show 0 if backend returns 0
  const schemaStabilityScore = getMetricValue(metricsData?.schema_drift?.stability_score, 0)

  // Extract file metadata
  const filename = metricsData?.latest_file?.original_filename || 'Unknown'
  const rawFileType = metricsData?.latest_file?.file_type || ''
  // Convert MIME type to simple display format (e.g., "text/csv" -> "CSV")
  let fileType = 'Unknown'
  if (rawFileType.includes('csv')) {
    fileType = 'CSV'
  } else if (rawFileType.includes('json')) {
    fileType = 'JSON'
  } else if (rawFileType.includes('xml')) {
    fileType = 'XML'
  } else if (rawFileType) {
    fileType = rawFileType.split('/').pop()?.toUpperCase() || 'Unknown'
  }
  const protocol = metricsData?.latest_file?.protocol || 'Unknown'

  // Extract file health status
  const fileHealthStatus = metricsData?.file_health?.status || 'HEALTHY'
  let fileHealthBadge = 'critical'
  if (fileHealthStatus === 'HEALTHY') {
    fileHealthBadge = 'healthy'
  } else if (fileHealthStatus === 'WARNING') {
    fileHealthBadge = 'warning'
  }
  const fileHealthLabel = fileHealthStatus.charAt(0) + fileHealthStatus.slice(1).toLowerCase()

  // Extract SLA status
  const slaStatus = metricsData?.file_sla_status?.status;
  let slaStatusBadge = 'no-sla'
  if (slaStatus === 'ON_TIME') {
    slaStatusBadge = 'on-time'
  } else if (slaStatus === 'AT_RISK') {
    slaStatusBadge = 'at-risk'
  } else if (slaStatus === 'BREACHED') {
    slaStatusBadge = 'breached'
  }
  let slaStatusLabel = 'No SLA configured'
  if (slaStatus === 'ON_TIME') {
    slaStatusLabel = 'On time'
  } else if (slaStatus === 'AT_RISK') {
    slaStatusLabel = 'At risk'
  } else if (slaStatus === 'BREACHED') {
    slaStatusLabel = 'Breached'
  }

  // Extract source impact
  const sourceImpactLevel = metricsData?.source_impact?.level || 'LOW'
  let sourceImpactBadge = 'low'
  if (sourceImpactLevel === 'CRITICAL') {
    sourceImpactBadge = 'critical'
  } else if (sourceImpactLevel === 'HIGH') {
    sourceImpactBadge = 'high'
  } else if (sourceImpactLevel === 'MEDIUM') {
    sourceImpactBadge = 'medium'
  }
  const sourceImpactLabel = sourceImpactLevel.charAt(0) + sourceImpactLevel.slice(1).toLowerCase()

  // Extract error breakdown
  const missingValuesCount = metricsData?.error_breakdown?.errors_by_type?.missing_values || 0
  const duplicatesCount = metricsData?.error_breakdown?.errors_by_type?.duplicates || 0
  const formatIssuesCount = metricsData?.error_breakdown?.errors_by_type?.format_issues || 0
  const rangeViolationsCount = metricsData?.error_breakdown?.errors_by_type?.range_violations || 0
  const conflictsCount = metricsData?.error_breakdown?.errors_by_type?.conflicts || 0

  // Extract consumers (Used By Models & Systems)
  const consumers = metricsData?.used_by?.consumers || []

  // Extract schema fields
  const schemaFields = metricsData?.schema_overview?.fields || []
  // Get trend data
  const completenessTrend = getTrendIndicator(metricsData?.metrics?.completeness_score?.trend)

  // Format chart data
  const completenessChartData = formatChartData(completenessData)
  const timelinessChartData = formatChartData(timelinessData)
  const pipelineChartData = formatChartData(pipelineSuccessData)
  const trustScoreChartData = formatChartData(trustScoreData)
  // Schema drift has different structure - use recent_activity
  const schemaStabilityChartData = schemaDriftData?.recent_activity?.map((activity: any) => ({
    time: new Date(activity.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    value: 100 - (activity.changes * 2) // Simple conversion: fewer changes = higher stability
  })) || []

  // Error rate time series from API
  const errorsWarningsData = metricsData?.error_rate_time_series?.data_points?.map((point: any) => ({
    time: new Date(point.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    value: point.error_rate
  })) || []

  const handleSchemaTimeRangeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as 'week' | 'month' | 'quarter' | 'year'
    setSchemaTimeRange(value)
  }

  const handleTrendTimeRangeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as '24h' | '7d' | '30d'
    const daysBack = value === '24h' ? 1 : value === '7d' ? 7 : 30
    setTrendTimeRange(value)
    setTrendDaysBack(daysBack)
  }

  return (
    <div className="file-details-view">
      {/* Header */}
      <FileDetailsHeader
        sourceName={source.source_name}
        filename={filename}
        onBack={onBack}
      />

      {/* Combined File Info and Metrics Section */}
      <div className="file-info-metrics-combined">
        {/* File Info */}
        <div className="file-details-main">
          <div className="file-title-section">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <polyline points="10 9 9 9 8 9"/>
            </svg>
            <h2 className="file-title">{filename}</h2>
          </div>
          <div className="file-trust-score-main">
            <div className="trust-score-label-main">File Trust Score</div>
            <div className="trust-score-value-main">
              <span 
                className="score-main"
                style={{
                  color: trustScore >= 95 ? '#22c55e' : trustScore >= 90 ? '#F0B100' : '#FB2C36'
                }}
              >
                {Math.round(trustScore)}
              </span>
              <span className="score-total-main">/100</span>
            </div>
          </div>
        </div>
        <div className="file-badges-row">
          <span className={`status-badge ${fileHealthBadge}`}>{fileHealthLabel}</span>
          <span className="status-badge file-type">{fileType}</span>
          <span className="status-badge protocol">{protocol}</span>
        </div>


        {/* Metrics Row */}
        <FileMetricsRow
          recordCount={recordCount}
          lastLoaded={lastLoaded}
          slaStatusBadge={slaStatusBadge}
          slaStatusLabel={slaStatusLabel}
          sourceImpactBadge={sourceImpactBadge}
          sourceImpactLabel={sourceImpactLabel}
        />

        {/* Metrics Section */}
        <MetricsGrid
          completenessScore={completenessScore}
          timelinessScore={timelinessScore}
          qualityScore={qualityScore}
          schemaScore={schemaScore}
          duplicatesScore={duplicatesScore}
          completenessTrend={completenessTrend}
        />

        {/* Errors and Warnings */}
        <ErrorsSection
          missingValuesCount={missingValuesCount}
          duplicatesCount={duplicatesCount}
          formatIssuesCount={formatIssuesCount}
          rangeViolationsCount={rangeViolationsCount}
          conflictsCount={conflictsCount}
          schemaDriftChanges={schemaDriftChanges}
        />

        {/* Used By Models & Systems */}
        <UsedBySection consumers={consumers} />

        {/* Schema Overview */}
        <SchemaSection schemaFields={schemaFields} />
      </div>

      {/* Schema Stability & Drift Section */}
      <SchemaStabilitySection
        schemaStabilityScore={schemaStabilityScore}
        schemaStabilityChartData={schemaStabilityChartData}
        isSchemaLoading={isSchemaLoading}
        schemaTimeRange={schemaTimeRange}
        handleSchemaTimeRangeChange={handleSchemaTimeRangeChange}
      />

      {/* Trust History & Trend Analysis Section */}
      <TrendAnalysisSection
        completenessScore={completenessScore}
        completenessTrend={completenessTrend}
        completenessChartData={completenessChartData}
        timelinessChartData={timelinessChartData}
        pipelineChartData={pipelineChartData}
        errorsWarningsData={errorsWarningsData}
        isCompletenessLoading={isCompletenessLoading}
        trendTimeRange={trendTimeRange}
        handleTrendTimeRangeChange={handleTrendTimeRangeChange}
      />

      {/* Trust Score Section - Title on left, Dropdown on right */}
      <div className="trust-score-chart-section">
        <div className="trust-score-header">
          <h3 className="trust-score-title">Trust Score</h3>
          <div className="trust-score-time-selector">
            <select className="trust-score-dropdown" value={trendTimeRange} onChange={handleTrendTimeRangeChange}>
              <option value="24h">Last 24 hours</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
            </select>
          </div>
        </div>

        <div className="trust-score-chart-container">
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={trustScoreChartData.length > 0 ? trustScoreChartData : [{ time: 'No data', value: 0 }]}>
              <defs>
                <linearGradient id="colorTrustScore" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#22c55e" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="time"
                tick={{ fontSize: 12, fill: '#6b7280' }}
                axisLine={{ stroke: '#e5e7eb' }}
              />
              <YAxis
                domain={[90, 100]}
                tick={{ fontSize: 12, fill: '#6b7280' }}
                axisLine={{ stroke: '#e5e7eb' }}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  padding: '10px'
                }}
                labelStyle={{ color: '#374151', fontWeight: 600 }}
                formatter={(value: any) => typeof value === 'number' ? [`${value.toFixed(1)}%`, 'Trust Score'] : ['', '']}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#22c55e"
                strokeWidth={3}
                fillOpacity={1}
                fill="url(#colorTrustScore)"
                animationDuration={1500}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  )
}

export default FileDetailsView
